import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Users, Factory } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import api from "@/lib/api";

const INV_IMG = "https://images.unsplash.com/photo-1510519138101-570d1dca3d66?crop=entropy&cs=srgb&fm=jpg&w=1600";

const PILLARS = [
  { n: "01", k: "OEM / ODM", sub: "Engineering revenue + IP" },
  { n: "02", k: "Home Automation", sub: "Direct + dealer" },
  { n: "03", k: "Industrial", sub: "Service contracts" },
  { n: "04", k: "SaaS portfolio", sub: "Recurring revenue" },
  { n: "05", k: "Marketplace", sub: "Tiered pricing" },
  { n: "06", k: "Franchise", sub: "Pan-India footprint" },
  { n: "07", k: "Affiliate", sub: "Distribution leverage" },
  { n: "08", k: "Incubation", sub: "Optionality" },
];

// Stats kept here are facts we can stand behind without a founder check.
// Soft claims ("Weeks", "India-first", a "40+ SKUs" aspiration) were removed
// rather than left as filler — better to under-show than over-claim to investors.
// SKU count is fetched live so it stays honest as the catalog grows.

export default function Investors() {
  usePageTitle("Investors");
  const [skuCount, setSkuCount] = useState(null);
  useEffect(() => {
    api.get("/products").then(({ data }) => setSkuCount(data.length)).catch(() => {});
  }, []);

  const STATS = [
    { v: "6", k: "Engineering domains in-house" },
    { v: skuCount != null ? String(skuCount) : "—", k: "Smart-home SKUs shipping" },
    { v: "10%", k: "Default affiliate share" },
  ];

  return (
    <div>
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-25"
             style={{ backgroundImage: `linear-gradient(0deg, #0A0B10 35%, rgba(10,11,16,0.3) 100%), url(${INV_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-mono">Investors</div>
          <h1 className="font-display text-4xl lg:text-6xl mt-3 tracking-tighter max-w-4xl">
            One engine, <span className="gradient-text">many revenue lines</span>.
          </h1>
          <p className="mt-6 text-white/70 max-w-2xl leading-relaxed">
            WeAutomate is built as a flywheel. Our OEM/ODM work trains a deep, cross-domain engineering team.
            That team builds our automation systems and SaaS products. Those products stock a marketplace.
            The marketplace powers a franchise and affiliate network across India. And that network surfaces
            the next OEM brief. Each line funds and feeds the next — an India-first compounding loop, not a single bet.
          </p>
          <Link to="/contact" className="mt-10 inline-flex items-center gap-2 px-6 py-3.5 rounded-full gradient-bg font-medium">
            Request the investor deck <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section className="py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Revenue lines</div>
          <h2 className="font-display text-4xl mt-3 tracking-tighter max-w-2xl">
            Eight engines, <span className="text-accent">one company</span>.
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PILLARS.map(({ n, k, sub }) => (
              <div key={n} className="glass rounded-2xl p-6 border-l-2 border-white/10 hover:border-accent/50 transition-colors">
                <div className="font-display text-white/30 text-3xl">{n}</div>
                <div className="mt-3 text-sm font-medium text-white">{k}</div>
                <div className="text-xs text-white/50 mt-1">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono mb-8">By the numbers</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {STATS.map(({ v, k }) => (
              <div key={k} className="glass rounded-2xl p-6">
                <div className="font-display text-3xl text-white">{v}</div>
                <div className="text-sm text-white/60 mt-2">{k}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid md:grid-cols-3 gap-6">
          {[
            { icon: TrendingUp, t: "Market", v: "Indian smart home + BMS TAM is growing 25%+ YoY." },
            { icon: Users,     t: "Team",   v: "Self-taught cross-domain founders + a 25+ person bench." },
            { icon: Factory,   t: "Moat",   v: "Manufacturing × Distribution × Software, owned end-to-end." },
          ].map((c) => (
            <div key={c.t} className="glass rounded-2xl p-7">
              <c.icon className="w-5 h-5 text-accent" />
              <div className="mt-4 font-display text-lg">{c.t}</div>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{c.v}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
